
import React from 'react';
import { Edit2, Save, X, Clock, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { ProjectCombobox } from '../ui/ProjectCombobox';
import { formatHoursMinutes } from '../../utils/timeUtils';
import { Project } from '@/hooks/useProjects';

interface TimeEntry {
  id: string;
  user_id: string;
  project_id: string;
  start_time: string;
  end_time: string | null;
  duration_minutes: number | null;
  description: string | null;
  is_billable: boolean;
}

interface TimeEntryItemProps {
  entry: TimeEntry;
  getProjectName: (projectId: string) => string;
  projects: Project[];
  isEditing: boolean;
  editValues: { start: string; end: string; description: string; date: string; projectId: string };
  onEditStart: (entry: TimeEntry) => void;
  onEditSave: (entryId: string) => void;
  onEditCancel: () => void;
  onEditValueChange: (field: 'start' | 'end' | 'description' | 'date' | 'projectId', value: string) => void;
  onDeleteEntry: (entryId: string) => void;
}

export const TimeEntryItem: React.FC<TimeEntryItemProps> = ({
  entry,
  getProjectName,
  projects,
  isEditing,
  editValues,
  onEditStart,
  onEditSave,
  onEditCancel,
  onEditValueChange,
  onDeleteEntry,
}) => {
  return (
    <div className="bg-white rounded-lg p-3 border" data-testid="time-entry-item">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="font-medium text-sm" data-testid="time-entry-project-name">
            {getProjectName(entry.project_id)}
          </div>
          <div className="text-xs text-muted-foreground">
            {entry.description || 'Aucune description'}
          </div>
          
          {isEditing ? (
            <div className="flex flex-col gap-2 mt-2">
              {/* Sélecteur de projet avec recherche */}
              <ProjectCombobox
                projects={projects}
                value={editValues.projectId}
                onValueChange={(value) => onEditValueChange('projectId', value)}
                placeholder="Choisir un projet"
                triggerClassName="h-8 text-xs"
              />

              {/* Description */}
              <Input
                type="text"
                placeholder="Description"
                value={editValues.description}
                onChange={(e) => onEditValueChange('description', e.target.value)}
                className="text-xs"
              />

              {/* Date */}
              <Input
                type="date"
                value={editValues.date}
                onChange={(e) => onEditValueChange('date', e.target.value)}
                className="w-40 h-8 text-xs"
              />

              {/* Heures */}
              <div className="flex items-center gap-2">
                <Input
                  type="time"
                  value={editValues.start}
                  onChange={(e) => onEditValueChange('start', e.target.value)}
                  className="w-24 h-8 text-xs"
                />
                <span className="text-xs">à</span>
                <Input
                  type="time"
                  value={editValues.end}
                  onChange={(e) => onEditValueChange('end', e.target.value)}
                  className="w-24 h-8 text-xs"
                />

                {/* Actions */}
                <Button
                  size="sm"
                  onClick={() => onEditSave(entry.id)}
                  className="h-8 px-2"
                  data-testid="time-entry-save-btn"
                >
                  <Save className="h-3 w-3" />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={onEditCancel}
                  className="h-8 px-2"
                >
                  <X className="h-3 w-3" />
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => onDeleteEntry(entry.id)}
                  className="h-8 px-2"
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2 mt-1">
              <span className="text-xs text-muted-foreground">
                {new Date(entry.start_time).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })} - {entry.end_time ? new Date(entry.end_time).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : 'En cours'}
              </span>
              <Badge variant="outline" className="text-[0.65rem] flex items-center gap-1">
                <Clock className="h-3 w-3" /> {formatHoursMinutes(entry.duration_minutes || 0)}
              </Badge>

              <Button
                size="sm"
                variant="ghost"
                onClick={() => onEditStart(entry)}
                className="h-6 px-1"
                data-testid="time-entry-edit-btn"
              >
                <Edit2 className="h-3 w-3" />
              </Button>
            </div>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          <Badge variant={entry.is_billable ? 'default' : 'outline'} className="text-xs">
            {entry.is_billable ? 'Facturable' : 'Non facturable'}
          </Badge>
        </div>
      </div>
    </div>
  );
};
